import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type Me } from './api';
import { forgetPush } from '../taskforce/push';

/** The signed-in user, or null when signed out. */
export function useMe() {
  return useQuery<Me | null>({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await api<Me>('/api/me');
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
  });
}

export function useSignOut() {
  const qc = useQueryClient();
  return async () => {
    // Push stops here with the sign-in (the server forgets this browser with it).
    forgetPush();
    try {
      await api('/api/auth/logout', { method: 'POST' });
    } finally {
      qc.clear();
      qc.setQueryData(['me'], null);
    }
  };
}

/** Remember me keeps only the sign-in name or email in this browser (never a password). */
export const LAST_SIGN_IN_KEY = 'attDash.lastSignIn';
export const lastSignIn = {
  get(): string | null {
    try {
      return localStorage.getItem(LAST_SIGN_IN_KEY);
    } catch {
      return null;
    }
  },
  set(v: string) {
    try {
      localStorage.setItem(LAST_SIGN_IN_KEY, v);
    } catch {
      /* storage unavailable */
    }
  },
  clear() {
    try {
      localStorage.removeItem(LAST_SIGN_IN_KEY);
      // The prototype's encrypted password store, if this browser still has one.
      localStorage.removeItem('attDash.saved');
    } catch {
      /* storage unavailable */
    }
  },
};

export const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
export const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
